import React, { useEffect, useState } from 'react';
import { ChatList } from './ChatList';
import { ChatDetail } from './ChatDetail';
import { aiAgentApi } from '../../../lib/aiAgentApi';
import { toast } from 'sonner';

/**
 * Two-state stack: list ↔ detail. Matches the wireframe (top screen = list,
 * bottom screen = chat detail). On mobile/iPad-like flows the user goes
 * back to the list with the arrow in the header.
 *
 * When a `resume` payload is passed (e.g. after a Stripe top-up returning to
 * chat), open the referenced conv directly and forward the primer prompt to
 * ChatDetail so it auto-sends once on mount.
 */
export const ChatsView = ({ resume, onResumeConsumed, blocked, personaName, onTopUp, refreshWallet }) => {
  const [activeConv, setActiveConv] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [resumePrompt, setResumePrompt] = useState(null);

  // Honour a resume request from outside (Stripe success page etc.).
  useEffect(() => {
    if (!resume?.conv_id) return;
    (async () => {
      try {
        const detail = await aiAgentApi.getConversation(resume.conv_id, { limit: 1, include_tool: false });
        if (detail?.conversation) {
          setActiveConv(detail.conversation);
          setResumePrompt(resume.prompt || null);
        }
      } catch {
        toast.error('Could not reopen that chat');
      } finally {
        onResumeConsumed?.();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resume?.conv_id]);

  const openThread = (conv) => setActiveConv(conv);

  const newChat = async () => {
    try {
      const c = await aiAgentApi.createConversation();
      setActiveConv(c);
      setRefreshKey((k) => k + 1);
    } catch {
      toast.error('Could not start a new chat');
    }
  };

  if (activeConv) {
    return (
      <ChatDetail
        conv={activeConv}
        resumePrompt={resumePrompt}
        onResumeConsumed={() => setResumePrompt(null)}
        onBack={() => { setActiveConv(null); setRefreshKey((k) => k + 1); }}
        onChanged={() => setRefreshKey((k) => k + 1)}
        blocked={blocked}
        personaName={personaName}
        onTopUp={onTopUp}
        refreshWallet={refreshWallet}
      />
    );
  }
  return <ChatList refreshKey={refreshKey} onOpenThread={openThread} onNewChat={newChat} />;
};
